import type { SlideGeneric } from "@types"

/** If no frame is produced within this long (page hidden or occluded),
 *  proceed anyway so a turn can never stall behind a paint. */
const PAINT_WAIT_CAP_MS = 150

/** Resolves after the browser has had a chance to paint the current frame. */
export const afterNextPaint = () =>
  new Promise<void>(resolve => {
    const cap = setTimeout(resolve, PAINT_WAIT_CAP_MS)
    requestAnimationFrame(() =>
      setTimeout(() => {
        clearTimeout(cap)
        resolve()
      }, 0),
    )
  })

/** Waits for `promise`, but never longer than `ms`. */
export const withCap = <T>(promise: Promise<T>, ms: number) =>
  Promise.race([
    promise.then(() => undefined),
    new Promise<void>(resolve => setTimeout(resolve, ms)),
  ])

export type WarmedImage = {
  /** Keep a reference for as long as the decoded bitmap should stay alive */
  element: HTMLImageElement
  /** Resolves once the picture is decoded (or failed); never rejects */
  ready: Promise<void>
}

/**
 * Fetches and decodes a slide picture ahead of time. The attributes are set
 * in the same order and with the same values as the on-screen <img>, so the
 * browser resolves the same srcset candidate and the later render is a
 * memory-cache and decode-cache hit.
 */
export const warmImage = (
  slide: Pick<SlideGeneric, "image" | "imageSrcSet">,
  sizes?: string,
): WarmedImage => {
  const element = new Image()
  element.decoding = "async"
  if (slide.imageSrcSet && sizes) {
    element.sizes = sizes
    element.srcset = slide.imageSrcSet
  }
  element.src = slide.image
  const loaded =
    typeof element.decode === "function"
      ? element.decode()
      : new Promise<void>(resolve => {
          element.onload = () => resolve()
          element.onerror = () => resolve()
        })
  return {
    element,
    ready: loaded.then(
      () => undefined,
      () => undefined,
    ),
  }
}

/**
 * Runs `fn` once the main thread is idle, but not before `delayMs` and not
 * later than about half a second after that, so pre-rolling never competes
 * with the transition it follows and still happens while the player reads.
 */
export const onIdle = (fn: () => void, delayMs: number) => {
  let idleHandle: number | undefined
  const timer = setTimeout(() => {
    if (typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(fn, { timeout: 500 })
    } else {
      fn()
    }
  }, delayMs)
  return {
    cancel: () => {
      clearTimeout(timer)
      if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle)
    },
  }
}
